<template>
  <Layout :step="step" :steps="9">
    <v-container class="questionnaire-container">
      <Loader v-if="loading" :loading="loading"></Loader>

      <button
        class="default-btn text"
        @click="onNavBackClicked"
      >Back</button>

      <StepBar v-if="step" :step="step" :steps="steps" />

      <Step1 v-if="step === 1" @finished="advanceQuestionnaire"></Step1>
      <Step2 v-if="step === 2" @finished="advanceQuestionnaire"></Step2>
      <Step3 v-if="step === 3" @finished="advanceQuestionnaire"></Step3>
      <Step4 v-if="step === 4" @finished="advanceQuestionnaire"></Step4>
      <Step5 v-if="step === 5" @finished="advanceQuestionnaire"></Step5>
      <Step6 v-if="step === 6" @finished="advanceQuestionnaire"></Step6>
      <Step7 v-if="step === 7" @finished="advanceQuestionnaire"></Step7>
      <Step8 v-if="step === 8" @finished="advanceQuestionnaire"></Step8>
      <Step9 v-if="step === 9" @finished="advanceQuestionnaire"></Step9>
      <Step10 v-if="step === 10" @finished="advanceQuestionnaire"></Step10>
    </v-container>
  </Layout>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import Layout from '@/modules/questionnaire/components/ui/Layout'

import StepBar from './StepBar'
import Step1 from './1_DealTypeStep'
import Step2 from './2_PracticeStep'
import Step3 from './3_DealSizeStep'
import Step4 from './4_LoanTypeStep'
import Step5 from './5_RepayLoanStep'
import Step6 from './6_CreditScoreStep'
import Step7 from './7_LenderFeaturesStep'
import Step8 from './8_IssuesStep'
import Step9 from './9_DeadlineStep'
import Step10 from './10_PhoneStep'

export default {
  name: 'QuestionnairePage',
  components: {
    Layout,
    StepBar,
    Step1,
    Step2,
    Step3,
    Step4,
    Step5,
    Step6,
    Step7,
    Step8,
    Step9,
    Step10,
  },
  props: {
    query: Object,
  },
  data: () => ({
    loading: false,
    steps: 10,
  }),
  computed: {
    ...mapGetters('feathers', ['apiDeals', 'apiUsers']),
    ...mapGetters('questionnaireStore', ['selectedCommercial', 'selectedSBA']),
    ...mapState('authStore', {
      userData: 'userData',
    }),
    ...mapState('questionnaireStore', {
      dealId: 'dealId',
      matchData: 'matchData',
      newPhone: 'newPhone',
      newExtension: 'newExtension',
    }),

    step () {
      const { name } = this.$route

      if (name === 'ChooseDealType') return 1
      if (name === 'ChoosePractice') return 2
      if (name === 'ChooseDealSize') return 3
      if (name === 'ChooseLoanType') return 4
      if (name === 'ChooseRepayLoan') return 5
      if (name === 'ChooseCreditScore') return 6
      if (name === 'ChooseLenderFeatures') return 7
      if (name === 'ChooseIssues') return 8
      if (name === 'ChooseDeadline') return 9
      if (name === 'EnterPhoneNumber') return 10

      return 0
    },
  },

  async created () {
    this.loading = true

    if (this.userData) {
      const latestDeals = await this.getRecentDeals(this.userData.id)
      if (latestDeals.length >= 3) return this.$router.push({ name: 'LenderResults' })
    }

    window.addEventListener('beforeunload', this.checkBeforeUserCancelsPage)

    if (this.step !== 1) {
      if (this.query.dealType) {
        this.updateMatchData({
          dealType: this.query.dealType,
        })

        this.loading = false
        return this.$router.replace({ name: 'ChoosePractice' })
      }

      if (this.step !== 0) {
        this.addAlert({
          type: 'warning',
          message: 'You cannot start a questionnaire midway. You have been brought instead to step 1',
        })
      }

      this.loading = false
      return this.$router.replace({ name: 'ChooseDealType' })
    }

    this.loading = false
  },

  beforeDestroy () {
    this.clearMatchData()
    window.removeEventListener('beforeunload', this.checkBeforeUserCancelsPage)
  },

  methods: {
    ...mapActions('feathers', ['getRecentDeals']),
    ...mapActions(
      'questionnaireStore',
      ['clearMatchData', 'setQuizCompleted', 'updateMatchData', 'setDealId', 'setLendersInDeal'],
    ),

    checkBeforeUserCancelsPage (event) {
      event.preventDefault()
      event.returnValue = ''
    },

    advanceQuestionnaire () {
      if (this.step <= 1) return this.$router.push({ name: 'ChoosePractice' })
      if (this.step <= 2) return this.$router.push({ name: 'ChooseDealSize' })
      if (this.step <= 3) return this.$router.push({ name: 'ChooseLoanType' })
      if (this.step <= 4 && !this.selectedCommercial && !this.selectedSBA) {
        return this.$router.push({ name: 'ChooseRepayLoan' })
      }
      if (this.step <= 5) return this.$router.push({ name: 'ChooseCreditScore' })
      if (this.step <= 6 && !this.selectedCommercial) {
        return this.$router.push({ name: 'ChooseLenderFeatures' })
      }
      if (this.step <= 7) return this.$router.push({ name: 'ChooseIssues' })
      if (this.step <= 8) return this.$router.push({ name: 'ChooseDeadline' })

      if (this.step <= 9 && !(this.userData && this.userData.phone)) {
        return this.$router.push({ name: 'EnterPhoneNumber' })
      }

      return this.completeQuestionnaire()
    },

    isQuizFullyCompleted () {
      // const { selectedCommercial, selectedSBA, matchData } = this
      const { matchData } = this

      return (
        (matchData.dealType || []).length &&
        matchData.practiceType &&
        matchData.dealSize &&
        ('loanType' in matchData) &&
        (matchData.creditScore) &&
        ('hasRegulatoryIssues' in matchData) &&
        ('hasDisclosureEvents' in matchData) &&
        matchData.speedOfClosing
      )
    },

    async completeQuestionnaire () {
      this.loading = true

      if (!this.isQuizFullyCompleted()) {
        this.addAlert({
          type: 'warning',
          message: 'Sorry, there was an error submitting the questionnaire. Please try again.',
        })

        this.loading = false
        return this.$router.push({ name: 'ChooseDealType' })
      }

      this.setQuizCompleted()

      try {
        if (this.userData && this.newPhone) {
          await this.apiUsers.patch(this.userData.id, {
            phone: this.newPhone,
            extension: this.newExtension,
          })
        }

        const data = await this.apiDeals.create({
          ...this.matchData,
          userId: (this.userData && this.userData.id) || null,
          source: 'questionnaire',
        })

        this.setLendersInDeal((data.matches || []).length)
        this.setDealId(data.id)
      } catch (e) {
        this.addErrorAlert(`There was an issue when processing the questionnaire: ${e.message}`)
        this.loading = false
        return
      }

      try {
        this.loading = false

        const next = this.userData
          ? { name: 'Matches', params: { dealId: this.dealId } }
          : { name: 'SignUp' }

        return this.$router.push(next)
      } catch (err) {
        this.addAlert({ type: 'error', message: err.message })
      }

      this.loading = false
    },

    onNavBackClicked () {
      if (this.step > 1) return this.$router.go(-1)

      const go = window.confirm('Are you sure you wish to exit? You will have to fill everything out again.')
      if (go) this.$router.push({ name: 'Home' })
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep {
  .questionnaire-container > .container {
    display: flex;
    flex-direction: column;
    align-items: center;

    .default-btn {
      min-width: 200px;
    }
  }

  .option-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;

    max-width: 90%;
    margin-bottom: 20px;

    &.column {
      flex-direction: column;

      .default-btn:not(:first-child) {
        margin-top: 25px;
      }
    }

    .default-btn {
      flex-direction: column;

      min-height: 45px;
      width: 400px;
      max-width: 100%;
      padding: 5px 20px;

      font-size: 16px;

      @include md { font-size: 18px; }
      @include lg { font-size: 20px; }

      span {
        font-size: 10px;
        letter-spacing: 0.15px;
        line-height: 16px;
        text-transform: uppercase;

        @include md { font-size: 12px; }
      }
    }
  }

  h2 {
    display: flex;
    justify-content: center;

    margin: 35px 0 65px;

    color: #707070;
    font-size: 18px;
    text-align: center;

    @include md {
      font-size: 24px;
    }

    @include lg {
      font-size: 36px;
    }
  }

  h2 + p {
    margin-top: -50px;
    margin-bottom: 50px;

    font-family: "Avenir Book", sans-serif;
    text-align: center;
  }
}
</style>
